import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CommunityPost } from "../interfaces/post.interface";
import { usePostStore } from "../../../../../store/postStore";

type Props = {
  post: CommunityPost | null;
  isOpen: boolean;
  onClose: () => void;
};

const EditPostModal: React.FC<Props> = ({ post, isOpen, onClose }) => {
  const [body, setBody] = useState("");
  const [removeMedia, setRemoveMedia] = useState<number[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);

  const updatePost = usePostStore((s) => s.updatePost);
  const isSaving = usePostStore((s) => s.isSaving);

  useEffect(() => {
    if (post) {
      setBody(post.body ?? "");
      setRemoveMedia([]);
      setNewFiles([]);
    }
  }, [post]);

  const handleSave = async () => {
    if (!post) return;

    const success = await updatePost(post.id, {
      body,
      remove_media: removeMedia,
      media: newFiles,
    });

    if (success) {
      onClose();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    setNewFiles((prev) => [...prev, ...selected].slice(0, 4));
    e.target.value = "";
  };

  const existingMedia = (post?.media ?? []).filter(
    (m) => !removeMedia.includes(m.id)
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white dark:bg-dark_grey p-6 shadow-xl">
                <Dialog.Title className="text-xl font-medium mb-4">
                  Edit Post
                </Dialog.Title>

                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border p-3 dark:bg-deepSlate"
                  placeholder="What's on your mind?"
                />

                {/* Existing media */}
                {existingMedia.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {existingMedia.map((media) => (
                      <div
                        key={media.id}
                        className="relative aspect-square overflow-hidden rounded-lg"
                      >
                        {media.type === "video" ? (
                          <video
                            src={media.url}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <img
                            src={media.url}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        )}

                        <button
                          onClick={() =>
                            setRemoveMedia((prev) => [...prev, media.id])
                          }
                          className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* New files */}
                {newFiles.length > 0 && (
                  <div className="mt-3 text-sm text-gray-500">
                    {newFiles.length} new file(s) to upload
                  </div>
                )}

                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleFileSelect}
                  className="mt-3 block w-full text-sm"
                />

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 rounded border"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleSave}
                    disabled={isSaving || (!body.trim() && existingMedia.length === 0)}
                    className="px-4 py-2 rounded bg-primary-600 text-white disabled:opacity-50"
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditPostModal;